'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Loader2, Phone, Mail, CheckCircle, TrendingUp, Calendar, AlertCircle, Clock } from 'lucide-react';
import PageLoader from '@/components/PageLoader';
import { createClient } from '@/lib/supabase/client';

// --- Rate limit config ---
const MAX_ATTEMPTS = 5;           // max attempts before client-side cooldown
const ATTEMPT_WINDOW_MS = 60_000; // 60 s window for counting attempts
const COOLDOWN_SECONDS = 60;      // cooldown duration after hitting limit

export default function LoginPage() {
  const router = useRouter();
  const { signIn, signInWithGoogle, signInWithFacebook, signInWithPhone, verifyOtp } = useAuth();

  const [activeTab, setActiveTab] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'facebook' | null>(null);
  const [error, setError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [shakeError, setShakeError] = useState(false);

  // Rate-limit state
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const attemptTimestamps = useRef<number[]>([]);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const cooldownEnd = useRef<number>(0);

  const formRef = useRef<HTMLFormElement>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownEnd.current = Date.now() + seconds * 1000;
    setCooldownRemaining(seconds);
    cooldownTimer.current = setInterval(() => {
      const remaining = Math.ceil((cooldownEnd.current - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(cooldownTimer.current!);
        cooldownTimer.current = null;
        setCooldownRemaining(0);
        attemptTimestamps.current = [];
        setError('');
      } else {
        setCooldownRemaining(remaining);
      }
    }, 1000);
  }, []);

  /** Returns true if the attempt is allowed, false if rate-limited */
  const checkRateLimit = useCallback((): boolean => {
    if (cooldownRemaining > 0) return false;
    const now = Date.now();
    // Purge old timestamps outside the window
    attemptTimestamps.current = attemptTimestamps.current.filter(
      (t) => now - t < ATTEMPT_WINDOW_MS
    );
    if (attemptTimestamps.current.length >= MAX_ATTEMPTS) {
      startCooldown(COOLDOWN_SECONDS);
      setError(`অনেক বেশি চেষ্টা করা হয়েছে। ${COOLDOWN_SECONDS} সেকেন্ড অপেক্ষা করুন।`);
      triggerShake();
      return false;
    }
    attemptTimestamps.current.push(now);
    return true;
  }, [cooldownRemaining, startCooldown]);

  const triggerShake = () => {
    setShakeError(true);
    setTimeout(() => setShakeError(false), 500);
  };

  const setErrorWithShake = (msg: string) => {
    setError(msg);
    triggerShake();
  };

  const handle429 = useCallback((cooldown = COOLDOWN_SECONDS) => {
    startCooldown(cooldown);
    setError(`অনেক বেশি চেষ্টা করা হয়েছে। ${cooldown} সেকেন্ড অপেক্ষা করুন।`);
    triggerShake();
  }, [startCooldown]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setErrorWithShake('ইমেইল এবং পাসওয়ার্ড দিন।'); return; }
    if (!checkRateLimit()) return;
    setLoading(true); setError('');
    try {
      const data = await signIn(email, password);

      // Check role from all possible locations — user_metadata, raw_user_meta_data, app_metadata
      let role =
        data?.user?.user_metadata?.role ||
        data?.user?.raw_user_meta_data?.role ||
        data?.user?.app_metadata?.role;

      // If role still not found, fetch from user_profiles table (authoritative source)
      if (!role && data?.user?.id) {
        try {
          const supabase = createClient();
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', data.user.id)
            .single();
          if (profileData?.role) {
            role = profileData.role;
          }
        } catch {
          // silently fall through to default
        }
      }

      role = role || 'farmer';

      setLoginSuccess(true);
      toast.success('সফলভাবে লগইন হয়েছে! ড্যাশবোর্ডে যাচ্ছেন...', {
        description: 'Welcome back to AgroGuard AI.',
        duration: 3000,
      });
      setRedirecting(true);
      setTimeout(() => {
        router.push(role === 'admin' ? '/admin-dashboard' : '/farmer-dashboard');
        router.refresh();
      }, 800);
    } catch (err: any) {
      const msg = err?.message || '';
      if (err?.status === 429 || msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
        handle429();
      } else if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setErrorWithShake('ইমেইল বা পাসওয়ার্ড সঠিক নয়। আবার চেষ্টা করুন।');
      } else if (msg.includes('Email not confirmed')) {
        setErrorWithShake('ইমেইল যাচাই করা হয়নি। আপনার ইনবক্স চেক করুন।');
      } else {
        setErrorWithShake(msg || 'লগইন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।');
      }
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setSocialLoading('google'); setError('');
    try { await signInWithGoogle(); }
    catch (err: any) { setErrorWithShake(err?.message || 'Google লগইন ব্যর্থ হয়েছে।'); setSocialLoading(null); }
  };

  const handleFacebookLogin = async () => {
    setSocialLoading('facebook'); setError('');
    try { await signInWithFacebook(); }
    catch (err: any) { setErrorWithShake(err?.message || 'Facebook লগইন ব্যর্থ হয়েছে।'); setSocialLoading(null); }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) { setErrorWithShake('ফোন নম্বর দিন।'); return; }
    const phoneRegex = /^\+[1-9]\d{7,14}$/;
    if (!phoneRegex.test(phone)) {
      setErrorWithShake('সঠিক আন্তর্জাতিক ফরম্যাটে ফোন নম্বর দিন। যেমন: +8801XXXXXXXXX');
      return;
    }
    if (!checkRateLimit()) return;
    setLoading(true); setError('');
    try {
      await signInWithPhone(phone);
      setOtpSent(true);
      toast.success('OTP পাঠানো হয়েছে! আপনার ফোন চেক করুন।', {
        description: 'একটি ৬-সংখ্যার কোড আপনার ফোনে পাঠানো হয়েছে।',
        duration: 5000,
      });
    } catch (err: any) {
      const msg = err?.message || '';
      if (err?.status === 429 || msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
        handle429();
      } else if (msg.includes('not supported') || msg.includes('SMS')) {
        setErrorWithShake('SMS সেবা এই মুহূর্তে উপলব্ধ নেই। ইমেইল দিয়ে লগইন করুন।');
      } else {
        setErrorWithShake(msg || 'OTP পাঠাতে ব্যর্থ হয়েছে।');
      }
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) { setErrorWithShake('OTP কোড দিন।'); return; }
    if (otp.length !== 6) { setErrorWithShake('৬-সংখ্যার OTP কোড দিন।'); return; }
    if (!checkRateLimit()) return;
    setLoading(true); setError('');
    try {
      const data = await verifyOtp(phone, otp);

      // Check role from all possible locations
      let role =
        data?.user?.user_metadata?.role ||
        data?.user?.raw_user_meta_data?.role ||
        data?.user?.app_metadata?.role;

      // If role still not found, fetch from user_profiles table
      if (!role && data?.user?.id) {
        try {
          const supabase = createClient();
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', data.user.id)
            .single();
          if (profileData?.role) {
            role = profileData.role;
          }
        } catch {
          // silently fall through to default
        }
      }

      role = role || 'farmer';

      setLoginSuccess(true);
      toast.success('সফলভাবে লগইন হয়েছে! ড্যাশবোর্ডে যাচ্ছেন...', { duration: 3000 });
      setRedirecting(true);
      setTimeout(() => {
        router.push(role === 'admin' ? '/admin-dashboard' : '/farmer-dashboard');
        router.refresh();
      }, 800);
    } catch (err: any) {
      const msg = err?.message || '';
      if (err?.status === 429 || msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
        handle429();
      } else if (msg.includes('Token has expired') || msg.includes('expired')) {
        setErrorWithShake('OTP মেয়াদ শেষ হয়ে গেছে। নতুন OTP পাঠান।');
      } else if (msg.includes('invalid') || msg.includes('Token')) {
        setErrorWithShake('OTP কোড সঠিক নয়। আবার চেষ্টা করুন।');
      } else {
        setErrorWithShake(msg || 'OTP যাচাই ব্যর্থ হয়েছে।');
      }
      setLoading(false);
    }
  };

  const isRateLimited = cooldownRemaining > 0;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Full-screen redirect loader overlay */}
      {redirecting && <PageLoader message="ড্যাশবোর্ডে যাচ্ছেন" />}

      {/* Left: Form Panel */}
      <div className="flex-1 flex items-center justify-center p-5 sm:p-10 bg-white min-h-screen lg:min-h-0">
        <div className="w-full max-w-md slide-up">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-6 sm:mb-8 hover:opacity-90 transition-opacity duration-200">
            <AppLogo size={40} />
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">AgroGuard AI</p>
              <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">লগইন করুন</h1>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-5 sm:mb-6">
            অ্যাকাউন্ট নেই?{' '}
            <Link href="/register" className="text-green-600 font-semibold hover:underline transition-all duration-200 hover:text-green-700">এখানে তৈরি করুন</Link>
          </p>

          {/* Tab switcher */}
          <div className="flex border border-gray-200 rounded-2xl p-1 mb-5 sm:mb-6 bg-gray-50">
            <button
              type="button"
              onClick={() => { setActiveTab('email'); setError(''); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 ${activeTab === 'email' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}
            >
              <Mail size={15} /> ইমেইল
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('phone'); setError(''); setOtpSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 ${activeTab === 'phone' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}
            >
              <Phone size={15} /> মোবাইল নম্বর
            </button>
          </div>

          {/* Rate limit cooldown banner */}
          {isRateLimited && (
            <div className="bg-orange-50 border border-orange-300 rounded-xl px-4 py-3 text-sm text-orange-800 mb-4 flex items-center gap-3">
              <Clock size={18} className="text-orange-500 shrink-0 animate-pulse" />
              <div className="flex-1">
                <p className="font-semibold">অনেক বেশি চেষ্টা করা হয়েছে</p>
                <p className="text-xs text-orange-600 mt-0.5">
                  আবার চেষ্টা করুন{' '}
                  <span className="font-bold text-orange-700 tabular-nums">{cooldownRemaining}</span>{' '}
                  সেকেন্ড পরে
                </p>
              </div>
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 border-2 border-orange-300 shrink-0">
                <span className="text-sm font-bold text-orange-700 tabular-nums">{cooldownRemaining}</span>
              </div>
            </div>
          )}

          {/* Error with shake (non-rate-limit errors) */}
          {error && !isRateLimited && (
            <div className={`bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-4 flex items-start gap-2 error-slide-in ${shakeError ? 'shake' : ''}`}>
              <span className="text-red-500 mt-0.5 shrink-0">⚠</span>
              <span>{error}</span>
            </div>
          )}

          {/* Success inline message */}
          {loginSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 mb-4 flex items-center gap-2 success-pop">
              <CheckCircle size={16} className="text-green-600 check-pop shrink-0" />
              <span>সফলভাবে লগইন হয়েছে!</span>
            </div>
          )}

          {/* Email Form */}
          {activeTab === 'email' && (
            <form ref={formRef} onSubmit={handleEmailLogin} className="space-y-4 scale-in">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  ইমেইল ঠিকানা <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailTouched(true);
                      setEmailValid(e.target.value.includes('@') && e.target.value.includes('.'));
                    }}
                    onBlur={() => setEmailTouched(true)}
                    placeholder="example@email.com"
                    disabled={isRateLimited}
                    className={`w-full px-4 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all duration-200 hover:border-gray-300 field-highlight disabled:opacity-50 disabled:cursor-not-allowed ${
                      emailTouched && email && !emailValid
                        ? 'border-red-300 ring-2 ring-red-100 focus:border-red-400 focus:ring-red-100'
                        : emailValid
                        ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20' :'border-gray-200 focus:border-green-400 focus:ring-green-100'
                    }`}
                    required
                  />
                  {emailValid && (
                    <CheckCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-green-500 check-pop" />
                  )}
                  {emailTouched && email && !emailValid && (
                    <AlertCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-500" />
                  )}
                </div>
                {emailTouched && email && !emailValid && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1 error-slide-in">
                    <AlertCircle size={11} /> সঠিক ইমেইল ফরম্যাট দিন (যেমন: example@email.com)
                  </p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-semibold text-gray-700">
                    পাসওয়ার্ড <span className="text-red-500">*</span>
                  </label>
                  <Link href="/forgot-password" className="text-xs text-green-600 hover:underline font-medium transition-colors duration-200 hover:text-green-700">পাসওয়ার্ড ভুলে গেছেন?</Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordValid(e.target.value.length >= 6);
                    }}
                    placeholder="••••••••"
                    disabled={isRateLimited}
                    className={`w-full px-4 pr-12 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all duration-200 hover:border-gray-300 field-highlight disabled:opacity-50 disabled:cursor-not-allowed ${
                      passwordValid ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20' : 'border-gray-200 focus:border-green-400 focus:ring-green-100'
                    }`}
                    required
                  />
                  {passwordValid && (
                    <CheckCircle size={15} className="absolute right-9 top-1/2 -translate-y-1/2 text-green-500 check-pop" />
                  )}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-0 h-full min-w-[48px] flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all duration-200 hover:scale-110 active:scale-90"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || redirecting || isRateLimited}
                className="w-full min-h-[48px] py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95 btn-ripple"
              >
                {isRateLimited ? (
                  <>
                    <Clock size={18} className="animate-pulse" />
                    {cooldownRemaining}s পরে চেষ্টা করুন
                  </>
                ) : loading ? (
                  <><Loader2 size={18} className="animate-spin" /> লগইন হচ্ছে...</>
                ) : loginSuccess ? (
                  <><CheckCircle size={18} className="check-pop" /> সফল!</>
                ) : (
                  'লগইন করুন'
                )}
              </button>
            </form>
          )}

          {/* Phone Form */}
          {activeTab === 'phone' && (
            <div className="space-y-4 scale-in">
              {!otpSent ? (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">ফোন নম্বর</label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+8801XXXXXXXXX"
                        disabled={isRateLimited}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-all duration-200 hover:border-gray-300 field-highlight disabled:opacity-50 disabled:cursor-not-allowed"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">আন্তর্জাতিক ফরম্যাটে দিন: +8801XXXXXXXXX</p>
                  </div>
                  <button type="submit" disabled={loading || isRateLimited} className="w-full min-h-[48px] py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95 btn-ripple">
                    {isRateLimited ? (
                      <><Clock size={18} className="animate-pulse" /> {cooldownRemaining}s পরে চেষ্টা করুন</>
                    ) : loading ? (
                      <><Loader2 size={18} className="animate-spin" /> পাঠানো হচ্ছে...</>
                    ) : 'OTP পাঠান'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4 fade-in">
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle size={15} className="text-green-600 shrink-0" />
                    <span><strong>{phone}</strong> নম্বরে OTP পাঠানো হয়েছে।</span>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">OTP কোড</label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="6-সংখ্যার কোড"
                      maxLength={6}
                      disabled={isRateLimited}
                      className={`w-full px-4 py-3 border rounded-xl text-sm text-center tracking-widest font-mono text-lg focus:outline-none focus:ring-2 transition-all duration-200 hover:border-gray-300 field-highlight disabled:opacity-50 disabled:cursor-not-allowed ${
                        otp.length === 6 ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20' : 'border-gray-200 focus:border-green-400 focus:ring-green-100'
                      }`}
                      required
                    />
                  </div>
                  <button type="submit" disabled={loading || redirecting || isRateLimited} className="w-full min-h-[48px] py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95 btn-ripple">
                    {isRateLimited ? (
                      <><Clock size={18} className="animate-pulse" /> {cooldownRemaining}s পরে চেষ্টা করুন</>
                    ) : loading ? (
                      <><Loader2 size={18} className="animate-spin" /> যাচাই হচ্ছে...</>
                    ) : 'যাচাই করুন'}
                  </button>
                  <button type="button" onClick={() => { setOtpSent(false); setOtp(''); setError(''); }} className="w-full min-h-[48px] text-sm text-gray-500 hover:text-green-600 transition-all duration-200 flex items-center justify-center hover:-translate-y-0.5 active:scale-95">
                    নম্বর পরিবর্তন করুন
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium">অথবা</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleGoogleLogin}
              disabled={!!socialLoading || loading || redirecting}
              className="flex items-center justify-center gap-2 min-h-[48px] px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5 active:scale-95 active:translate-y-0"
            >
              {socialLoading === 'google' ? <Loader2 size={16} className="animate-spin" /> : (
                <svg width="18" height="18" viewBox="0 0 24 24" className="transition-transform duration-200 group-hover:scale-110">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Google
            </button>
            <button
              onClick={handleFacebookLogin}
              disabled={!!socialLoading || loading || redirecting}
              className="flex items-center justify-center gap-2 min-h-[48px] px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5 active:scale-95 active:translate-y-0"
            >
              {socialLoading === 'facebook' ? <Loader2 size={16} className="animate-spin" /> : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              )}
              Facebook
            </button>
          </div>

          {/* Demo credentials */}
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">ডেমো অ্যাকাউন্ট</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white rounded-lg p-2.5 border border-amber-100">
                <p className="font-bold text-gray-700 mb-1">👨‍🌾 কৃষক</p>
                <p className="text-gray-500 font-mono">farmer@agroguard.ai</p>
                <p className="text-gray-500 font-mono">farmer123</p>
              </div>
              <div className="bg-white rounded-lg p-2.5 border border-amber-100">
                <p className="font-bold text-gray-700 mb-1">🛡️ অ্যাডমিন</p>
                <p className="text-gray-500 font-mono">admin@agroguard.ai</p>
                <p className="text-gray-500 font-mono">admin123</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Agricultural Imagery Panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200&q=80')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/60 via-green-800/40 to-transparent" />

        <div className="relative z-10 w-full h-full flex flex-col justify-between p-10">
          <div className="self-end max-w-xs">
            <div className="bg-gray-900/85 backdrop-blur-sm rounded-2xl p-4 text-white shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-amber-400 rounded-full" />
                <span className="text-xs text-gray-300 font-medium">ফসল বিশ্লেষণ</span>
              </div>
              <p className="text-sm font-bold mb-1">ধানের পাতার রোগ শনাক্ত হয়েছে</p>
              <p className="text-xs text-green-400 font-semibold">নির্ভুলতা: ৯২%</p>
            </div>
          </div>

          <div className="self-start max-w-xs">
            <div className="bg-gray-900/85 backdrop-blur-sm rounded-2xl p-4 text-white shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} className="text-green-400" />
                <span className="text-xs text-gray-300 font-medium">কৃষক কার্যক্রম</span>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">কৃষক:</span>
                  <span className="font-semibold">রহিম মিয়া</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">ফসল:</span>
                  <span className="font-semibold">ধান</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">স্ট্যাটাস:</span>
                  <span className="text-green-400 font-semibold flex items-center gap-1">
                    <CheckCircle size={11} /> বিশ্লেষণ সম্পন্ন
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="self-end max-w-xs">
            <div className="bg-gray-900/85 backdrop-blur-sm rounded-2xl p-4 text-white shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={14} className="text-green-400" />
                <span className="text-xs text-gray-300 font-medium">ফসল ক্যালেন্ডার</span>
              </div>
              <p className="text-xs font-semibold mb-2 text-green-300">ধান রোপণের মৌসুম</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full w-3/5 bg-green-400 rounded-full" />
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">মার্চ – মে</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom image strip */}
      <div className="lg:hidden h-32 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800&q=70')` }}
        />
        <div className="absolute inset-0 bg-green-900/50" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <p className="text-white text-sm font-semibold opacity-90">AI-চালিত ফসল রোগ শনাক্তকরণ</p>
        </div>
      </div>
    </div>
  );
}
